import { useState, useRef } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Upload, Trash2, ImageIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api } from "@/lib/api"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { sectionClass, sectionHeaderClass } from "@/lib/styleClasses"

export const LogoSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)

    const logoUrl = areal.logoS3Key ? `/api/areals/${areal.id}/logo` : null

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setUploading(true)
        try {
            const fd = new FormData()
            fd.append("file", file)
            await api.upload<ArealResponse>(`/areals/${areal.id}/logo`, fd)
            toast.success(t("admin.settings.logoUploaded"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        } catch {
            toast.error(t("admin.settings.logoUploadFailed"))
        } finally {
            setUploading(false)
            if (fileInputRef.current) fileInputRef.current.value = ""
        }
    }

    const deleteMutation = useMutation({
        mutationFn: () => api.delete<ArealResponse>(`/areals/${areal.id}/logo`),
        onSuccess: () => {
            toast.success(t("admin.settings.logoDeleted"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        },
        onError: () => toast.error(t("admin.settings.logoDeleteFailed")),
    })

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionLogo")}</h2>
            <p className="text-sm font-bold text-slate-500">{t("admin.settings.logoDescription")}</p>

            <div className="flex items-center gap-5 flex-wrap">
                <div className="shrink-0 w-24 h-24 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shadow-sm">
                    {logoUrl ? (
                        <img
                            key={areal.logoS3Key}
                            src={logoUrl}
                            alt={areal.name}
                            className="w-full h-full object-contain p-1"
                        />
                    ) : (
                        <ImageIcon className="size-8 text-slate-300" />
                    )}
                </div>

                <div className="flex gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 px-4 py-2 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                    >
                        <Upload className="size-4" />
                        {uploading ? t("admin.settings.uploading") : t("admin.settings.uploadLogo")}
                    </button>
                    {logoUrl && (
                        <button
                            type="button"
                            onClick={() => {
                                if (window.confirm(t("admin.settings.confirmDeleteLogo"))) {
                                    deleteMutation.mutate()
                                }
                            }}
                            disabled={deleteMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 rounded-[28px] bg-red-50 text-red-500 font-bold text-sm hover:bg-red-100 transition-colors active:scale-95 disabled:opacity-50"
                        >
                            <Trash2 className="size-4" />
                            {t("admin.settings.deleteLogo")}
                        </button>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                />
            </div>
        </div>
    )
}
